"use client";
import React, { useEffect, useState } from "react";
import { useRouter, usePathname, useSearchParams } from "next/navigation";
import {
  DropdownOption,
  DropdownSection,
  InputDate,
  InputSelect,
} from "@ama-pt/agora-design-system";
import { useTranslations } from "next-intl";

function DateFilterComponent() {
  const t = useTranslations("Filters");
  const router = useRouter();
  const pathname = usePathname();
  const searchParams = useSearchParams();

  // InputDate is not fully controlled — clicking outside while active clears its
  // internal display even though the URL value is still set. These counters force
  // a remount (via key) to restore the displayed value from the URL param.
  const [startKey, setStartKey] = useState(0);
  const [endKey, setEndKey] = useState(0);

  const startDateValue = searchParams?.get("startDate")?.split("T")[0];
  const endDateValue = searchParams?.get("endDate")?.split("T")[0];

  const handleStartDateChange = (event: any) => {
    if (!event?.target?.value) {
      if (startDateValue) setStartKey((k) => k + 1);
      return;
    }

    const dateStr = event.target.value;
    let formattedDate = dateStr;

    if (dateStr.includes("/")) {
      const [day, month, year] = dateStr.split("/");
      formattedDate = `${year}-${month}-${day}T00:00:00Z`;
    } else if (dateStr.includes("-")) {
      formattedDate = `${dateStr}T00:00:00Z`;
    }

    const params = new URLSearchParams(searchParams?.toString());
    params.set("startDate", formattedDate);
    params.set("page", "1");
    router.push(`${pathname}?${params.toString()}`);
  };

  const handleEndDateChange = (event: any) => {
    if (!event?.target?.value) {
      if (endDateValue) setEndKey((k) => k + 1);
      return;
    }

    const dateStr = event.target.value;
    let formattedDate = dateStr;

    if (dateStr.includes("/")) {
      const [day, month, year] = dateStr.split("/");
      formattedDate = `${year}-${month}-${day}T23:59:59Z`;
    } else if (dateStr.includes("-")) {
      formattedDate = `${dateStr}T23:59:59Z`;
    }

    const params = new URLSearchParams(searchParams?.toString());
    params.set("endDate", formattedDate);
    params.set("page", "1");
    router.push(`${pathname}?${params.toString()}`);
  };

  const ptBRDateLabels = {
    monthsLabels: {
      jan: t("months.jan"),
      feb: t("months.feb"),
      mar: t("months.mar"),
      apr: t("months.apr"),
      may: t("months.may"),
      jun: t("months.jun"),
      jul: t("months.jul"),
      aug: t("months.aug"),
      set: t("months.set"),
      oct: t("months.oct"),
      nov: t("months.nov"),
      dec: t("months.dec"),
    },
    weekdaysLabels: {
      sun: t("weekdays.sun"),
      mon: t("weekdays.mon"),
      tue: t("weekdays.tue"),
      wed: t("weekdays.wed"),
      thu: t("weekdays.thu"),
      fri: t("weekdays.fri"),
      sat: t("weekdays.sat"),
    },
    todayLabel: t("datePicker.today"),
    cancelLabel: t("datePicker.cancel"),
    okLabel: t("datePicker.ok"),
  };

  return (
    <div className="space-y-16">
      <InputDate
        key={`start-${startDateValue}-${startKey}`}
        previousYearAriaLabel={t("datePicker.previousYear")}
        previousMonthAriaLabel={t("datePicker.previousMonth")}
        nextMonthAriaLabel={t("datePicker.nextMonth")}
        nextYearAriaLabel={t("datePicker.nextYear")}
        selectedDayAriaLabel={t("datePicker.selectedDay")}
        todayDayAriaLabel={t("datePicker.todayDay")}
        label={t("dateFrom")}
        value={startDateValue}
        onChange={handleStartDateChange}
        dayInputPlaceholder={t("datePicker.dayPlaceholder")}
        monthInputPlaceholder={t("datePicker.monthPlaceholder")}
        yearInputPlaceholder={t("datePicker.yearPlaceholder")}
        calendarIconAriaLabel={t("datePicker.openCalendar")}
        {...ptBRDateLabels}
      />

      <InputDate
        key={`end-${endDateValue}-${endKey}`}
        previousYearAriaLabel={t("datePicker.previousYear")}
        previousMonthAriaLabel={t("datePicker.previousMonth")}
        nextMonthAriaLabel={t("datePicker.nextMonth")}
        nextYearAriaLabel={t("datePicker.nextYear")}
        selectedDayAriaLabel={t("datePicker.selectedDay")}
        todayDayAriaLabel={t("datePicker.todayDay")}
        label={t("dateTo")}
        value={endDateValue}
        onChange={handleEndDateChange}
        dayInputPlaceholder={t("datePicker.dayPlaceholder")}
        monthInputPlaceholder={t("datePicker.monthPlaceholder")}
        yearInputPlaceholder={t("datePicker.yearPlaceholder")}
        calendarIconAriaLabel={t("datePicker.openCalendar")}
        {...ptBRDateLabels}
      />
    </div>
  );
}

export default function ResponsiveFilters({
  serviceCounterData,
  activeDate,
  activePubAndRegion,
  activeImage,
  publicationTypeData = [],
  contentThemeData = [],
  regionData = [],
  selectedTheme,
  categoryData = [],
  selectedPublicationType,
  selectedRegion,
  activeCategory,
  selectedCategory,
}: {
  serviceCounterData?: any;
  activeDate?: boolean;
  activePubAndRegion?: boolean;
  activeImage?: boolean;
  publicationTypeData?: { value: string; label: string }[];
  contentThemeData?: { value: string; label: string }[];
  regionData?: { value: string; label: string }[];
  selectedTheme?: string;
  categoryData?: { value: string; label: string }[];
  selectedPublicationType?: string;
  selectedRegion?: string;
  activeCategory?: boolean;
  selectedCategory?: string;
}) {
  const t = useTranslations("Filters");
  const [isOpen, setIsOpen] = useState(true);
  const router = useRouter();
  const pathname = usePathname();
  const searchParams = useSearchParams();

  const district = searchParams?.get("district") || "";
  const municipality = searchParams?.get("municipality") || "";

  const [distritos, setDistritos] = useState<
    { value: string; label: string }[]
  >([]);
  const [municipios, setMunicipios] = useState<
    { value: string; label: string }[]
  >([]);

  useEffect(() => {
    if (serviceCounterData?.docs) {
      const uniqueDistricts = new Set<string>();

      serviceCounterData.docs.forEach((item: any) => {
        if (item.district) {
          uniqueDistricts.add(item.district);
        }
      });

      const districtOptions = Array.from(uniqueDistricts)
        .sort()
        .map((district) => ({
          value: district.toLowerCase().replace(/\s+/g, "_"),
          label: district,
        }));

      setDistritos(districtOptions);
    }
  }, [serviceCounterData]);

  useEffect(() => {
    if (serviceCounterData?.docs) {
      const filteredMunicipalities = new Set<string>();

      if (district) {
        const selectedDistrictLabel = distritos.find(
          (d) => d.value === district
        )?.label;

        serviceCounterData.docs.forEach((item: any) => {
          if (item.district === selectedDistrictLabel && item.municipality) {
            filteredMunicipalities.add(item.municipality);
          }
        });
      } else {
        serviceCounterData.docs.forEach((item: any) => {
          if (item.municipality) {
            filteredMunicipalities.add(item.municipality);
          }
        });
      }

      const municipalityOptions = Array.from(filteredMunicipalities)
        .sort()
        .map((municipality) => ({
          value: municipality.toLowerCase().replace(/\s+/g, "_"),
          label: municipality,
        }));

      setMunicipios(municipalityOptions);
    }
  }, [serviceCounterData, district, distritos]);

  const handleDistrictChange = (selected: any[]) => {
    const value = selected.length > 0 ? selected[0].value : undefined;
    const params = new URLSearchParams(searchParams?.toString());

    if (value) {
      params.set("district", value);
    } else {
      params.delete("district");
    }

    params.delete("municipality");
    params.set("page", "1");

    router.push(`${pathname}?${params.toString()}`);
  };

  const updateSingleFilter = (key: string, value: string | undefined) => {
    const params = new URLSearchParams(searchParams?.toString());

    if (value) {
      params.set(key, value);
    } else {
      params.delete(key);
    }

    params.set("page", "1");
    router.push(`${pathname}?${params.toString()}`);
  };

  const handleMunicipalityChange = (selected: any[]) => {
    const value = selected.length > 0 ? selected[0].value : undefined;
    updateSingleFilter("municipality", value);
  };

  const handlePublicationTypeChange = (selected: any[]) => {
    const value = selected.length > 0 ? selected[0].value : undefined;
    updateSingleFilter("publicationType", value);
  };

  const handleContentThemeChange = (selected: any[]) => {
    const value = selected.length > 0 ? selected[0].value : undefined;
    updateSingleFilter("contentTheme", value);
  };

  const handleRegionChange = (selected: any[]) => {
    const value = selected.length > 0 ? selected[0].value : undefined;
    updateSingleFilter("region", value);
  };

  const handleCategoryChange = (selected: any[]) => {
    const value = selected.length > 0 ? selected[0].value : undefined;
    updateSingleFilter("category", value);
  };

  const toggleAccordion = () => {
    setIsOpen(!isOpen);
  };

  const filtersContent = (
    <div className="space-y-24">
      {serviceCounterData && (
        <>
          <InputSelect
            label={t("Districts")}
            placeholder={t("selectOption")}
            hideSectionNames={true}
            dropdownAriaLabel={t("listDistricts")}
            type="text"
            onChange={handleDistrictChange}
            className="h-[500px]"
          >
            <DropdownSection
              className={`${
                distritos.length > 4
                  ? "h-[300px]"
                  : `h-[${distritos.length * 1}px]`
              }`}
              label={t("Districts")}
              name={t("districts")}
            >
              {distritos.map((item) => (
                <DropdownOption
                  key={item.value}
                  value={item.value}
                  selected={district === item.value}
                >
                  {item.label}
                </DropdownOption>
              ))}
            </DropdownSection>
          </InputSelect>

          <InputSelect
            label={t("Councils")}
            placeholder={t("selectOption")}
            hideSectionNames={true}
            dropdownAriaLabel={t("listCouncils")}
            type="text"
            onChange={handleMunicipalityChange}
          >
            <DropdownSection
              className={`${
                municipios.length > 4
                  ? "h-[300px]"
                  : `h-[${municipios.length * 1}px]`
              }`}
              label={t("Councils")}
              name={t("councils")}
            >
              {municipios.map((item) => (
                <DropdownOption
                  key={item.value}
                  value={item.value}
                  selected={municipality === item.value}
                >
                  {item.label}
                </DropdownOption>
              ))}
            </DropdownSection>
          </InputSelect>
        </>
      )}

      {activePubAndRegion && (
        <>
          {publicationTypeData.length > 0 && (
            <InputSelect
              label={t("typeContent")}
              placeholder={t("typeContent")}
              hideSectionNames={true}
              dropdownAriaLabel={t("listContents")}
              type="text"
              visibleCount={5}
              onChange={handlePublicationTypeChange}
            >
              <DropdownSection
                className={`h-[${(publicationTypeData.length ?? 4) * 62}px]`}
                label={t("typeContents")}
                name="publicationTypes"
              >
                {publicationTypeData.map((item) => (
                  <DropdownOption
                    key={item.value}
                    value={item.value}
                    selected={selectedPublicationType === item.value}
                  >
                    {item.label && t(item?.label.toLowerCase())}
                  </DropdownOption>
                ))}
              </DropdownSection>
            </InputSelect>
          )}

          {regionData.length > 0 && (
            <InputSelect
              label={t("region")}
              placeholder={t("selectRegion")}
              hideSectionNames={true}
              dropdownAriaLabel={t("listRegion")}
              type="text"
              onChange={handleRegionChange}
            >
              <DropdownSection
                className={`h-[${(publicationTypeData.length ?? 4) * 62}px]`}
                label={t("region")}
                name="regions"
              >
                {regionData.map((item) => (
                  <DropdownOption
                    key={item.value}
                    value={item.value}
                    selected={selectedRegion === item.value}
                  >
                    {item.label && t(item?.label.toLowerCase())}
                  </DropdownOption>
                ))}
              </DropdownSection>
            </InputSelect>
          )}
        </>
      )}
      {activeImage && (
        <>
          {contentThemeData.length > 0 && (
            <InputSelect
              label={t("themeContent")}
              placeholder={t("themeContentsLabel")}
              hideSectionNames={true}
              dropdownAriaLabel={t("listContents")}
              type="text"
              visibleCount={5}
              onChange={handleContentThemeChange}
            >
              <DropdownSection
                className={`h-[${(contentThemeData.length ?? 4) * 62}px]`}
                label={t("themeContentsLabel")}
                name="contentThemes"
              >
                {contentThemeData.map((item) => (
                  <DropdownOption
                    key={item.value}
                    value={item.value}
                    selected={selectedTheme === item.value}
                  >
                    {item.label && t(item?.label.toLowerCase())}
                  </DropdownOption>
                ))}
              </DropdownSection>
            </InputSelect>
          )}
        </>
      )}

      {activeDate && <DateFilterComponent />}

      {activeCategory && categoryData && categoryData.length > 0 && (
        <InputSelect
          label={t("category")}
          placeholder={t("selectCategory")}
          hideSectionNames={true}
          dropdownAriaLabel={t("listCategories")}
          type="text"
          onChange={handleCategoryChange}
        >
          <DropdownSection
            className={`h-[${(categoryData.length ?? 4) * 62}px]`}
            label={t("categories")}
            name="categories"
          >
            {categoryData.map((item) => (
              <DropdownOption
                key={item.value}
                value={item.value}
                selected={selectedCategory === item.value}
              >
                {item.label}
              </DropdownOption>
            ))}
          </DropdownSection>
        </InputSelect>
      )}
    </div>
  );

  return (
    <>
      <div className="xl:hidden">
        <button
          className="bg-primary-100 p-32 w-full flex items-center justify-between"
          onClick={toggleAccordion}
          aria-expanded={isOpen}
        >
          <span className="flex items-center">
            <svg
              width="24"
              height="24"
              viewBox="0 0 24 24"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                fillRule="evenodd"
                clipRule="evenodd"
                d="M1.0929 2.57912C1.25675 2.22596 1.61069 2 2.00001 2H22C22.3893 2 22.7433 2.22596 22.9071 2.57912C23.071 2.93229 23.015 3.34845 22.7636 3.64573L15 12.8261V21C15 21.3466 14.8206 21.6684 14.5257 21.8507C14.2309 22.0329 13.8628 22.0494 13.5528 21.8944L9.5528 19.8944C9.21402 19.725 9.00001 19.3788 9.00001 19V12.8261L1.23644 3.64573C0.985046 3.34845 0.929037 2.93229 1.0929 2.57912ZM4.15532 4L10.7636 11.8143C10.9162 11.9948 11 12.2236 11 12.46V18.382L13 19.382V12.46C13 12.2236 13.0838 11.9948 13.2364 11.8143L19.8447 4H4.15532Z"
                fill="#0E8668"
              />
            </svg>

            <span className="ml-16 text-l-bold">{t("filt")}</span>
          </span>
          <svg
            className={`w-24 h-24 transition-transform ${
              isOpen ? "rotate-180" : ""
            }`}
            width="24"
            height="24"
            viewBox="0 0 24 24"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path
              fillRule="evenodd"
              clipRule="evenodd"
              d="M11.2929 8.29289C11.6834 7.90237 12.3166 7.90237 12.7071 8.29289L18.7071 14.2929C19.0976 14.6834 19.0976 15.3166 18.7071 15.7071C18.3166 16.0976 17.6834 16.0976 17.2929 15.7071L12 10.4142L6.70711 15.7071C6.31658 16.0976 5.68342 16.0976 5.29289 15.7071C4.90237 15.3166 4.90237 14.6834 5.29289 14.2929L11.2929 8.29289Z"
              fill="#0E8668"
            />
          </svg>
        </button>

        {isOpen && <div className="mt-24">{filtersContent}</div>}
      </div>

      <div className="hidden xl:block">
        <h2 className="text-l-bold mb-24">{t("filt")}</h2>
        {filtersContent}
      </div>
    </>
  );
}
